import Dexie from 'dexie'
import { join, assoc, always, pipe, once } from 'ramda'
import moment from 'moment'

// Configuration de la base de données Dexie (compatible avec l'app React)
export const database = once(() => {
  const db = new Dexie('primview')

  // Version 1 - Schema initial
  db.version(1).stores({
    teams: '++id, name, insertedAt, enabled',
    dictionaries: '++name, insertedAt',
    properties: join(',', [
      'id',
      'obsolete',
      '[nsil+id]',
      '[address.zipCode+id]',
      '[type+id]',
      '[assistance.mandator.id+id]',
      '[nbNeededActions+id]',
      'lastOccupant.id',
      '[scheduledVisitAtSortAsc+scheduledVisitAt+scheduledVisitStatusSortAsc+scheduledVisitStatus+id]',
      '[scheduledVisitAtSortDesc+scheduledVisitAt+scheduledVisitStatusSortDesc+scheduledVisitStatus+id]',
      '[occupancySortDesc+planifiedAtSortDesc+scheduledVisitStatusSortAsc+planifiedAt+id]',
      '[occupancySortDesc+planifiedAtSortAsc+scheduledVisitStatusSortDesc+planifiedAt+id]',
      '[scheduledVisitStatusSortDesc+technicalManager.fullName+id]',
      '[scheduledVisitStatusSortAsc+technicalManager.fullName+id]',
    ]),
    users: '++id, fullName, firstName, lastName, enabled, team.id, team.name, team.code, insertedAt',
    interventionPoints: '++id, insertedAt, position, [position+id]',
    mutations: '++id, insertedAt, propertyId, entity, synced',
    controls: '++id, insertedAt, visit.property.id, interventionPoint.position, actionStatus',
    assistanceMandators: '++id, name',
    managerMandators: '++id, name',
    legalOwners: '++id, name',
    sync: 'id, type, synced',
    
    // Tables supplémentaires pour pwa-vue
    auth: 'key, timestamp'
  })

  // Version 2 - Amélioration de la table sync pour la synchronisation intelligente
  db.version(2).stores({
    teams: '++id, name, insertedAt, enabled',
    dictionaries: '++name, insertedAt',
    properties: join(',', [
      'id',
      'obsolete',
      '[nsil+id]',
      '[address.zipCode+id]',
      '[type+id]',
      '[assistance.mandator.id+id]',
      '[nbNeededActions+id]',
      'lastOccupant.id',
      '[scheduledVisitAtSortAsc+scheduledVisitAt+scheduledVisitStatusSortAsc+scheduledVisitStatus+id]',
      '[scheduledVisitAtSortDesc+scheduledVisitAt+scheduledVisitStatusSortDesc+scheduledVisitStatus+id]',
      '[occupancySortDesc+planifiedAtSortDesc+scheduledVisitStatusSortAsc+planifiedAt+id]',
      '[occupancySortDesc+planifiedAtSortAsc+scheduledVisitStatusSortDesc+planifiedAt+id]',
      '[scheduledVisitStatusSortDesc+technicalManager.fullName+id]',
      '[scheduledVisitStatusSortAsc+technicalManager.fullName+id]',
    ]),
    users: '++id, fullName, firstName, lastName, enabled, team.id, team.name, team.code, insertedAt',
    interventionPoints: '++id, insertedAt, position, [position+id]',
    mutations: '++id, insertedAt, propertyId, entity, synced',
    controls: '++id, insertedAt, visit.property.id, interventionPoint.position, actionStatus',
    assistanceMandators: '++id, name',
    managerMandators: '++id, name',
    legalOwners: '++id, name',
    // Nouvelle structure pour sync avec support de la synchronisation intelligente
    // Note: 'entity' est la clé primaire, les autres sont juste des propriétés
    sync: 'entity',
    
    // Tables supplémentaires pour pwa-vue
    auth: 'key, timestamp'
  })

  // Version 3 - Suppression des tables "actions" et "visits"
  db.version(3).stores({
    teams: '++id, name, insertedAt, enabled',
    dictionaries: '++name, insertedAt',
    properties: join(',', [
      'id',
      'obsolete',
      '[nsil+id]',
      '[address.zipCode+id]',
      '[type+id]',
      '[assistance.mandator.id+id]',
      '[nbNeededActions+id]',
      'lastOccupant.id',
      '[scheduledVisitAtSortAsc+scheduledVisitAt+scheduledVisitStatusSortAsc+scheduledVisitStatus+id]',
      '[scheduledVisitAtSortDesc+scheduledVisitAt+scheduledVisitStatusSortDesc+scheduledVisitStatus+id]',
      '[occupancySortDesc+planifiedAtSortDesc+scheduledVisitStatusSortAsc+planifiedAt+id]',
      '[occupancySortDesc+planifiedAtSortAsc+scheduledVisitStatusSortDesc+planifiedAt+id]',
      '[scheduledVisitStatusSortDesc+technicalManager.fullName+id]',
      '[scheduledVisitStatusSortAsc+technicalManager.fullName+id]',
    ]),
    users: '++id, fullName, firstName, lastName, enabled, team.id, team.name, team.code, insertedAt',
    interventionPoints: '++id, insertedAt, position, [position+id]',
    mutations: '++id, insertedAt, propertyId, entity, synced',
    controls: '++id, insertedAt, visit.property.id, interventionPoint.position, actionStatus',
    assistanceMandators: '++id, name',
    managerMandators: '++id, name',
    legalOwners: '++id, name',
    sync: 'entity',
    auth: 'key, timestamp',
    // Tables "visits" et "actions" supprimées
    visits: null,
    actions: null
  })

  // Version 4 - Optimisation du stockage : suppression des index orphelins et inutilisés
  //
  // Index supprimés sur properties :
  //   - 'obsolete'               : jamais utilisé dans les requêtes
  //   - '[nbNeededActions+id]'   : jamais utilisé dans les requêtes
  //   - 'lastOccupant.id'        : jamais utilisé comme critère de recherche
  //   - les 5 index composés *Desc / occupancySortDesc : les champs pré-calculés
  //     correspondants ne sont jamais lus dans le code JS (seuls *SortAsc le sont)
  //
  // Index conservés : id, nsil+id, address.zipCode+id, type+id,
  //   assistance.mandator.id+id, scheduledVisitAtSortAsc+..., scheduledVisitStatusSortAsc+...
  //
  // auth : suppression du champ 'timestamp' de l'index (non utilisé, expiration
  //   vérifiée via jwtDecode côté applicatif)
  db.version(4).stores({
    teams: '++id, name, insertedAt, enabled',
    dictionaries: '++name, insertedAt',
    properties: join(',', [
      'id',
      '[nsil+id]',
      '[address.zipCode+id]',
      '[type+id]',
      '[assistance.mandator.id+id]',
      '[scheduledVisitAtSortAsc+scheduledVisitAt+scheduledVisitStatusSortAsc+scheduledVisitStatus+id]',
      '[scheduledVisitStatusSortAsc+technicalManager.fullName+id]',
    ]),
    users: '++id, fullName, firstName, lastName, enabled, team.id, team.name, team.code, insertedAt',
    interventionPoints: '++id, insertedAt, position, [position+id]',
    mutations: '++id, insertedAt, propertyId, entity, synced',
    controls: '++id, visit.property.id, interventionPoint.position, actionStatus',
    assistanceMandators: '++id, name',
    managerMandators: '++id, name',
    legalOwners: '++id, name',
    sync: 'entity',
    auth: 'key',
  })

  return db
})

export const table = name => database().table(name)

// Fonctions d'accès aux tables (compatibles avec l'app React)
export const teams = pipe(always('teams'), table)
export const dictionaries = pipe(always('dictionaries'), table)
export const properties = pipe(always('properties'), table)
export const users = pipe(always('users'), table)
export const interventionPoints = pipe(always('interventionPoints'), table)
export const mutations = pipe(always('mutations'), table)
export const controls = pipe(always('controls'), table)
export const assistanceMandators = pipe(always('assistanceMandators'), table)
export const managerMandators = pipe(always('managerMandators'), table)
export const legalOwners = pipe(always('legalOwners'), table)
export const sync = pipe(always('sync'), table)

// Tables pour pwa-vue
export const auth = pipe(always('auth'), table)

// Fonctions utilitaires (compatibles avec l'app React)
export const save = table => data => table().put(assoc('insertedAt', moment().toISOString(), data))
export const saveMany = table => datas => table().bulkPut(datas)

const isDexieTable = inputTable => inputTable instanceof database().Table
export const storeExist = pipe(table, isDexieTable)

// Service Dexie pour remplacer IndexedDBService
class DexieService {
  constructor() {
    this.db = database()
    
    // Clés pour les données (compatibilité avec l'ancien service)
    this.SYNC_TIMESTAMP_KEY = 'scaprim_sync_timestamp'
    this.DICTIONARIES_KEY = 'scaprim_dictionaries'
    this.AUTH_TOKEN_KEY = 'authToken'
    this.REFRESH_TOKEN_KEY = 'refreshToken'
    this.USER_KEY = 'user'
    
    // Cache en mémoire des propriétés pour éviter de recharger depuis IndexedDB
    this.propertiesCache = null
    this.propertiesCacheTimestamp = null

    // Cache des résultats filtrés+triés par clé (filters+sort)
    // Permet un slicing instantané pour la pagination sans re-scanner IndexedDB
    this.filteredSortedCache = {}

    // Cache des IDs des mandants GPR de type CNR (lots GPR sans CAT qui restent visibles)
    // Chargé à la demande depuis la table managerMandators et invalidé après sync.
    this.cnrMandatorIdsCache = null
  }

  // Initialisation (automatique avec Dexie)
  async init() {
    try {
      // La base est déjà initialisée via la fonction database()
      return this.db
    } catch (error) {
      console.error('Erreur lors de l\'ouverture de la base Dexie:', error)
      throw error
    }
  }

  // === GESTION DES PROPRIÉTÉS ===

  async countProperties() {
    try {
      return await properties().count()
    } catch (error) {
      console.error('Erreur lors du comptage des propriétés:', error)
      return 0
    }
  }

  async addProperty(property) {
    if (!property.id) {
      console.error('Impossible d\'ajouter une propriété sans ID')
      return false
    }

    try {
      // Optimiser la propriété avant sauvegarde si l'optimiseur est disponible
      let optimizedProperty = property
      try {
        const { optimizePropertyForSort } = await import('../utils/propertyOptimizer')
        const visitStatuses = await this.getDictionary('visit_status')
        const visitStatusesArray = visitStatuses?.items || []
        optimizedProperty = optimizePropertyForSort(property, visitStatusesArray)
      } catch (optimizerError) {
        // Si l'optimiseur n'est pas disponible, utiliser la propriété telle quelle
        console.warn('Optimiseur non disponible, sauvegarde sans optimisation:', optimizerError)
      }
      
      await properties().put(optimizedProperty)
      
      // Invalider tous les caches (raw + filtré+trié)
      this._invalidateAllPropertyCaches()
      
      return true
    } catch (error) {
      console.error('Erreur lors de l\'ajout de la propriété:', error)
      return false
    }
  }

  async removeProperty(propertyId) {
    try {
      await properties().delete(propertyId)
      return true
    } catch (error) {
      console.error('Erreur lors de la suppression de la propriété:', error)
      return false
    }
  }

  async getProperty(propertyId) {
    return await this.getPropertyByUuid(propertyId)
  }

  async getPropertyByUuid(uuid) {
    try {
      const property = await properties().get(uuid)
      return property || null
    } catch (error) {
      console.error('Erreur lors de la récupération de la propriété par UUID:', error)
      return null
    }
  }

  async updatePropertyPlanification(propertyId, planifiedAt) {
    try {
      // Utiliser le service mutation pour gérer la mise à jour
      // et enregistrer automatiquement dans la table mutations
      const mutationService = await import('./mutationService')
      const result = await mutationService.default.updatePropertyPlanification(propertyId, planifiedAt)
      
      // Invalider tous les caches si la mise à jour a réussi
      if (result.success) {
        this._invalidateAllPropertyCaches()
      }
      
      return result.success
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la planification:', error)
      return false
    }
  }

  async savePropertiesBatch(propertiesArray) {
    try {
      const validProperties = propertiesArray.filter(property => property.id)
      
      // Optimiser toutes les propriétés avant sauvegarde
      let optimizedProperties = validProperties
      try {
        const { optimizePropertiesBatch } = await import('../utils/propertyOptimizer')
        const visitStatuses = await this.getDictionary('visit_status')
        const visitStatusesArray = visitStatuses?.items || []
        optimizedProperties = optimizePropertiesBatch(validProperties, visitStatusesArray)
      } catch (optimizerError) {
        console.warn('Optimiseur non disponible pour le batch, sauvegarde sans optimisation:', optimizerError)
      }
      
      await saveMany(properties)(optimizedProperties)
      
      // Invalider tous les caches (raw + filtré+trié)
      this._invalidateAllPropertyCaches()
      
      return true
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du batch de propriétés:', error)
      return false
    }
  }

  async getAllProperties() {
    try {
      // Utiliser each() (curseur) plutôt que toArray() (getAll) pour éviter
      // les UnknownError sur les gros datasets en production (buffer IPC/mémoire)
      const allProps = []
      await properties().each(prop => allProps.push(prop))
      return allProps
    } catch (error) {
      console.error('Erreur lors de la récupération de toutes les propriétés:', error)
      return []
    }
  }

  // === FILTRAGE DES PROPRIÉTÉS (optimisé côté client) ===

  async filterProperties(filters = {}) {
    try {
      // Utiliser le cache en mémoire si disponible
      let allProperties
      if (this.propertiesCache) {
        allProperties = this.propertiesCache
      } else {
        // Utiliser each() (curseur) plutôt que toArray() (getAll) pour éviter
        // les UnknownError sur les gros datasets en production (buffer IPC/mémoire)
        allProperties = []
        await properties().each(prop => allProperties.push(prop))
        this.propertiesCache = allProperties
        this.propertiesCacheTimestamp = Date.now()
      }

      // Pré-charger les IDs des mandants GPR de type CNR (lots GPR sans CAT qui restent visibles).
      // Chargé une seule fois et mis en cache jusqu'à la prochaine invalidation.
      if (!this.cnrMandatorIdsCache) {
        try {
          const cnrRows = await managerMandators().filter(m =>
            m.name && m.name.toUpperCase().includes('CNR')
          ).toArray()
          this.cnrMandatorIdsCache = new Set(
            cnrRows.map(m => (m.id || '').toLowerCase()).filter(Boolean)
          )
        } catch (e) {
          // En cas d'erreur, utiliser un Set vide (permissif : aucun lot CNR exclu par erreur)
          this.cnrMandatorIdsCache = new Set()
        }
      }
      
      const now = new Date()
      
      // Filtrer côté client (JavaScript natif - ultra rapide)
      const filtered = allProperties.filter(property => {
        // Exclure définitivement les lots FILS (coliving/célibatorium).
        // Un lot fils a scheduledVisitStatus = 'check-parent' : il n'est jamais
        // proposé à la visite directement — seul son lot père l'est.
        if (property.scheduledVisitStatus === 'check-parent') return false

        // Règle métier : les lots GPR sans CAT ne doivent pas apparaître, sauf CNR.
        // Un lot GPR-seulement a un mandant gestionnaire (manager) mais pas de mandant
        // d'assistance (assistance). Exception : les lots dont le mandant GPR est CNR
        // (Compagnie Nationale du Rhône) restent visibles car leur mandant programme
        // des visites pour tous les lots indépendamment du contrat CAT.
        {
          const hasAssistance = !!property.assistance?.mandator?.id
          const hasManager    = !!property.manager?.mandator?.id
          if (!hasAssistance && hasManager) {
            const mandatorId = (property.manager.mandator.id || '').toLowerCase()
            if (!this.cnrMandatorIdsCache.has(mandatorId)) return false
          }
        }

        // Filtrer par gestion active (managementEndedAt null ou dans le futur)
        if (property.managementEndedAt && new Date(property.managementEndedAt) < now) {
          return false
        }

        // Filtrer par équipe (comparaison lâche car filters.team est une string depuis l'URL)
        if (filters.team) {
          if (property.teams && property.teams.length > 0) {
            const hasMatchingTeam = property.teams.some(team => String(team.id) === String(filters.team))
            if (!hasMatchingTeam) return false
          } else {
            return false
          }
        }

        // Filtrer par statut/occupancy
        if (filters.status) {
          let expectedOccupancy
          if (filters.status === 'vacant') {
            expectedOccupancy = 'Vacant'
            // Un lot père dont un enfant est occupé a scheduledVisitStatus = 'occupied'
            // mais son champ occupancy peut rester 'Vacant' (réécrit par la sync MSSQL).
            // On utilise scheduledVisitStatus comme source de vérité prioritaire.
            if (property.scheduledVisitStatus === 'occupied') return false
          } else if (filters.status === 'occupé') {
            expectedOccupancy = 'Occupé'
          } else {
            expectedOccupancy = filters.status
          }
          
          if (property.occupancy !== expectedOccupancy) return false
        }

    // Filtrer par planification
    const hasPlanification = filters.hasPlanification === 'ok' || filters.hasPlanification === true
    const hasNotPlanification = filters.hasNotPlanification === 'ok' || filters.hasNotPlanification === true
    
    if (hasPlanification && hasNotPlanification) {
      // BOTH: on accepte tout
    } else if (hasPlanification && !hasNotPlanification) {
      // PLANIFIED: seulement les propriétés avec planification
      if (!property.planifiedAt) return false
      
      // Filtrer par date de planification si spécifiée
      if (filters.startPlanificationAt) {
        const startDate = new Date(filters.startPlanificationAt)
        const planifiedDate = new Date(property.planifiedAt)
        if (planifiedDate < startDate) return false
      }
      
      if (filters.endPlanificationAt) {
        const endDate = new Date(filters.endPlanificationAt)
        const planifiedDate = new Date(property.planifiedAt)
        if (planifiedDate > endDate) return false
      }
    } else if (hasNotPlanification && !hasPlanification) {
      // UNPLANIFIED: seulement les propriétés sans planification
      if (property.planifiedAt) return false
    }
    // Si aucun filtre de planification n'est actif, on continue avec les autres filtres

    // Filtrer par dates de visite programmée
    if (filters.startScheduledVisitAt) {
      if (!property.scheduledVisitAt) return false
      const startDate = new Date(filters.startScheduledVisitAt)
      const scheduledDate = new Date(property.scheduledVisitAt)
      if (scheduledDate < startDate) return false
    }

    if (filters.endScheduledVisitAt) {
      // 'today' est un mot-clé dynamique résolu à l'exécution pour éviter qu'une
      // valeur stockée dans localStorage devienne obsolète au fil des jours.
      const endDateStr = filters.endScheduledVisitAt === 'today'
        ? new Date().toISOString().split('T')[0]
        : filters.endScheduledVisitAt
      // Les lots sans date (jamais planifiés ou statut bloquant) restent toujours
      // visibles : seuls les lots dont la prochaine visite est dans le futur sont masqués.
      if (property.scheduledVisitAt) {
        const scheduledDate = new Date(property.scheduledVisitAt)
        if (scheduledDate > new Date(endDateStr)) return false
      }
    }

        // Exclure un ou plusieurs types (filtre invisible pour l'utilisateur)
        // excludeType peut être une string (ex: 'Y') ou un tableau (ex: ['Y', '$'])
        if (filters.excludeType) {
          const excluded = Array.isArray(filters.excludeType) ? filters.excludeType : [filters.excludeType]
          if (excluded.includes(property.type)) return false
        }

        // Filtrer par type de propriété
        if (filters.propertyType && property.type !== filters.propertyType) return false
        if (filters.type && property.type !== filters.type) return false

        // Filtrer par gestionnaire/manager
        if (filters.manager && property.technicalManager?.id !== filters.manager) return false

        // Filtrer par mandant d'assistance
        // Comparaison case-insensitive pour robustesse (UUIDs peuvent être en majuscules depuis MSSQL)
        if (filters.assistance && property.assistance?.mandator?.id?.toLowerCase() !== filters.assistance.toLowerCase()) return false

        // Filtrer par propriétaire légal
        if (filters.owner && property.legalOwner?.id !== filters.owner) return false

        // Filtrer par propriétaire de groupe/mandant gestionnaire
        // Comparaison case-insensitive pour robustesse (UUIDs peuvent être en majuscules depuis MSSQL)
        if (filters.groupOwner && property.manager?.mandator?.id?.toLowerCase() !== filters.groupOwner.toLowerCase()) return false

        // Filtrer par unité (businessUnit est une string, pas un objet)
        if (filters.unit && property.businessUnit !== filters.unit) return false

        // Filtrer par sous-unité (businessSubUnit est une string, pas un objet)
        if (filters.subUnit && property.businessSubUnit !== filters.subUnit) return false

        // Filtrer par NSIL (recherche textuelle)
        if (filters.nsil) {
          const nsilTerm = filters.nsil.toLowerCase()
          const propertyNsil = (property.nsil || '').toLowerCase()
          if (!propertyNsil.includes(nsilTerm)) return false
        }

        // Filtrer par ville/code postal (recherche textuelle)
        if (filters.city) {
          const cityTerm = filters.city.toLowerCase()
          const searchableLocation = [
            property.address?.city,
            property.address?.zipCode,
            property.address?.street
          ].filter(Boolean).join(' ').toLowerCase()
          
          if (!searchableLocation.includes(cityTerm)) return false
        }

        // Filtrer par recherche textuelle générale
        if (filters.search) {
          const searchTerm = filters.search.toLowerCase()
          const searchableText = [
            property.nsil,
            property.propertyCode,
            property.address?.street,
            property.address?.city,
            property.address?.zipCode
          ].filter(Boolean).join(' ').toLowerCase()
          
          if (!searchableText.includes(searchTerm)) return false
        }

        return true
      })
      
      return filtered
    } catch (error) {
      console.error('Erreur lors du filtrage des propriétés:', error)
      return []
    }
  }

  // Tri optimisé avec les index Dexie
  async getSortedProperties(filters = {}, sortColumn = null, sortDirection = 'Asc') {
    try {
      let query = properties()

      // Utiliser les index Dexie pour un tri optimisé seulement si l'index existe
      if (sortColumn && sortDirection !== 'None') {
        const column = this.getColumnConfig(sortColumn)
        if (column && column.sortDexieId) {
          try {
            // Essayer d'utiliser l'index Dexie approprié
            const indexKey = sortDirection === 'Asc' ? 
              column.sortDexieId.find(id => id.includes('Asc')) || column.sortDexieId[0] :
              column.sortDexieId.find(id => id.includes('Desc')) || column.sortDexieId[0]
            
            if (indexKey) {
              // Vérifier si l'index existe avant de l'utiliser
              const schema = database().schema
              const tableSchema = schema.tables.find(t => t.name === 'properties')
              if (tableSchema && tableSchema.indexes.some(idx => idx.keyPath === indexKey)) {
                query = properties().orderBy(indexKey)
                if (sortDirection === 'Desc' && !indexKey.includes('Desc')) {
                  query = query.reverse()
                }
              } else {
                // Index non trouvé, utiliser le tri côté client
                console.warn(`Index Dexie non trouvé: ${indexKey}, utilisation du tri côté client`)
                return await this.getSortedPropertiesClientSide(filters, sortColumn, sortDirection)
              }
            }
          } catch (indexError) {
            console.warn('Erreur avec l\'index Dexie, utilisation du tri côté client:', indexError)
            return await this.getSortedPropertiesClientSide(filters, sortColumn, sortDirection)
          }
        } else {
          // Pas de configuration d'index pour cette colonne, utiliser le tri côté client
          return await this.getSortedPropertiesClientSide(filters, sortColumn, sortDirection)
        }
      }

      // Appliquer les filtres après le tri pour maintenir l'ordre
      const allProperties = await query.toArray()
      
      return allProperties.filter(property => {
        // Filtrer par gestion active
        if (property.managementEndedAt && new Date(property.managementEndedAt) < new Date()) {
          return false
        }

        // Appliquer tous les filtres comme dans filterProperties
        return this.applyPropertyFilters(property, filters)
      })
    } catch (error) {
      console.error('Erreur lors du tri des propriétés:', error)
      // Fallback sur le tri côté client
      return await this.getSortedPropertiesClientSide(filters, sortColumn, sortDirection)
    }
  }

  // Tri côté client comme fallback
  async getSortedPropertiesClientSide(filters = {}, sortColumn = null, sortDirection = 'Asc') {
    try {
      // Récupérer toutes les propriétés filtrées
      const filteredProperties = await this.filterProperties(filters)
      
      // Appliquer le tri côté client
      if (sortColumn && sortDirection !== 'None') {
        // Pour le tri scheduledVisitAt, nous devons gérer l'asynchrone
        if (sortColumn === 'scheduledVisitAt') {
          // Récupérer le dictionnaire visit_status une seule fois
          const visitStatusDict = await this.getDictionary('visit_status')
          
          filteredProperties.sort((a, b) => {
            let multiplier = sortDirection === 'Desc' ? -1 : 1
            
            // Logique de tri pour scheduledVisitAt avec dictionnaire
            const valueA = this.getScheduledVisitSortValueSync(a, visitStatusDict)
            const valueB = this.getScheduledVisitSortValueSync(b, visitStatusDict)
            
            // Comparaison des valeurs
            if (valueA < valueB) return -1 * multiplier
            if (valueA > valueB) return 1 * multiplier
            
            // En cas d'égalité, trier par ID
            if (a.id < b.id) return -1
            if (a.id > b.id) return 1
            return 0
          })
        } else {
          // Tri synchrone pour les autres colonnes
          filteredProperties.sort((a, b) => {
            let valueA, valueB
            let multiplier = sortDirection === 'Desc' ? -1 : 1
            
            switch (sortColumn) {
              case 'nsil':
                valueA = a.nsil || ''
                valueB = b.nsil || ''
                break
                
              case 'zipCode':
                valueA = a.address?.zipCode || ''
                valueB = b.address?.zipCode || ''
                break
                
              case 'propertyType':
                valueA = a.type || ''
                valueB = b.type || ''
                break
                
              case 'assistanceMandator':
                valueA = a.assistance?.mandator?.name || ''
                valueB = b.assistance?.mandator?.name || ''
                break
                
              case 'technicalManager':
                valueA = a.technicalManager?.fullName || a.technicalManager?.firstName + ' ' + a.technicalManager?.lastName || ''
                valueB = b.technicalManager?.fullName || b.technicalManager?.firstName + ' ' + b.technicalManager?.lastName || ''
                break
                
              case 'planifiedAt':
                valueA = a.planifiedAt ? new Date(a.planifiedAt).getTime() : Number.MAX_SAFE_INTEGER
                valueB = b.planifiedAt ? new Date(b.planifiedAt).getTime() : Number.MAX_SAFE_INTEGER
                break
                
              default:
                return 0
            }
            
            // Comparaison des valeurs
            if (valueA < valueB) return -1 * multiplier
            if (valueA > valueB) return 1 * multiplier
            
            // En cas d'égalité, trier par ID
            if (a.id < b.id) return -1
            if (a.id > b.id) return 1
            return 0
          })
        }
      }
      
      return filteredProperties
    } catch (error) {
      console.error('Erreur lors du tri côté client:', error)
      return await this.filterProperties(filters)
    }
  }

  // Version synchrone de getScheduledVisitSortValue pour le tri côté client
  getScheduledVisitSortValueSync(property, visitStatusDict) {
    // Priorité 1: scheduledVisitStatus transformé via le dictionnaire
    if (property.scheduledVisitStatus && visitStatusDict) {
      // Transformer le statut via le dictionnaire
      const transformedStatus = this.getVisitStatusValueFromDict(property.scheduledVisitStatus, visitStatusDict)
      
      if (transformedStatus && transformedStatus !== property.scheduledVisitStatus) {
        // Utiliser l'ordre défini dans le dictionnaire visit_status
        // Les clés du dictionnaire définissent l'ordre de priorité
        const statusKeys = Object.keys(visitStatusDict)
        const statusIndex = statusKeys.findIndex(key => key === property.scheduledVisitStatus)
        return statusIndex !== -1 ? statusIndex + 1 : 999
      }
    }
    
    // Priorité 2: scheduledVisitAt comme date
    if (property.scheduledVisitAt) {
      return new Date(property.scheduledVisitAt).getTime()
    }
    
    // Pas de valeur de tri - mettre à la fin
    return Number.MAX_SAFE_INTEGER
  }

  // Valeur de tri pour scheduledVisitAt (logique exacte de l'app React)
  async getScheduledVisitSortValue(property) {
    // Priorité 1: scheduledVisitStatus transformé via le dictionnaire
    if (property.scheduledVisitStatus) {
      try {
        // Récupérer le dictionnaire visit_status
        const visitStatusDict = await this.getDictionary('visit_status')
        
        // Transformer le statut via le dictionnaire
        const transformedStatus = this.getVisitStatusValueFromDict(property.scheduledVisitStatus, visitStatusDict)
        
        if (transformedStatus && transformedStatus !== property.scheduledVisitStatus) {
          // Utiliser l'ordre défini dans le dictionnaire visit_status
          // Les clés du dictionnaire définissent l'ordre de priorité
          const statusKeys = Object.keys(visitStatusDict)
          const statusIndex = statusKeys.findIndex(key => key === property.scheduledVisitStatus)
          return statusIndex !== -1 ? statusIndex + 1 : 999
        }
      } catch (error) {
        console.warn('Erreur lors de la récupération du dictionnaire visit_status:', error)
      }
    }
    
    // Priorité 2: scheduledVisitAt comme date
    if (property.scheduledVisitAt) {
      return new Date(property.scheduledVisitAt).getTime()
    }
    
    // Pas de valeur de tri - mettre à la fin
    return Number.MAX_SAFE_INTEGER
  }

  // Méthode helper pour transformer un statut de visite via le dictionnaire
  getVisitStatusValueFromDict(scheduledVisitStatus, visitStatusDict) {
    if (!scheduledVisitStatus || !visitStatusDict) return ''
    
    // Si visitStatusDict est un tableau d'objets { key, value } (format de l'ancienne app)
    if (Array.isArray(visitStatusDict)) {
      const found = visitStatusDict.find(item => item.key === scheduledVisitStatus)
      return found ? found.value : ''
    }
    
    // Si visitStatusDict est un objet clé-valeur
    if (typeof visitStatusDict === 'object') {
      return visitStatusDict[scheduledVisitStatus] || ''
    }
    
    return ''
  }

  // Configuration des colonnes pour le tri Dexie (seulement les index qui existent)
  getColumnConfig(sortId) {
    const columns = {
      'nsil': { sortDexieId: ['nsil+id'] },
      'zipCode': { sortDexieId: ['address.zipCode+id'] },
      'propertyType': { sortDexieId: ['type+id'] },
      'assistanceMandator': { sortDexieId: ['assistance.mandator.id+id'] }
      // Les autres colonnes utiliseront le tri côté client
    }
    
    return columns[sortId]
  }

  // Méthode helper pour appliquer les filtres
  applyPropertyFilters(property, filters) {
    // Exclure définitivement les lots FILS (coliving/célibatorium).
    if (property.scheduledVisitStatus === 'check-parent') return false

    // Règle métier : les lots GPR sans CAT ne doivent pas apparaître, sauf CNR.
    // (même règle que dans filterProperties — chemin de tri alternatif)
    if (this.cnrMandatorIdsCache) {
      const hasAssistance = !!property.assistance?.mandator?.id
      const hasManager    = !!property.manager?.mandator?.id
      if (!hasAssistance && hasManager) {
        const mandatorId = (property.manager.mandator.id || '').toLowerCase()
        if (!this.cnrMandatorIdsCache.has(mandatorId)) return false
      }
    }

    // Filtrer par équipe (comparaison lâche car filters.team est une string depuis l'URL)
    if (filters.team) {
      if (property.teams && property.teams.length > 0) {
        const hasMatchingTeam = property.teams.some(team => String(team.id) === String(filters.team))
        if (!hasMatchingTeam) return false
      } else {
        return false
      }
    }

    // Filtrer par statut/occupancy
    if (filters.status) {
      let expectedOccupancy
      if (filters.status === 'vacant') {
        expectedOccupancy = 'Vacant'
        // Un lot père dont un enfant est occupé a scheduledVisitStatus = 'occupied'
        // mais son champ occupancy peut rester 'Vacant' (réécrit par la sync MSSQL).
        // On utilise scheduledVisitStatus comme source de vérité prioritaire.
        if (property.scheduledVisitStatus === 'occupied') return false
      } else if (filters.status === 'occupé') {
        expectedOccupancy = 'Occupé'
      } else {
        expectedOccupancy = filters.status
      }
      
      if (property.occupancy !== expectedOccupancy) return false
    }

    // Filtrer par planification
    const hasPlanification = filters.hasPlanification === 'ok' || filters.hasPlanification === true
    const hasNotPlanification = filters.hasNotPlanification === 'ok' || filters.hasNotPlanification === true
    
    if (hasPlanification && hasNotPlanification) {
      // BOTH: on accepte tout
    } else if (hasPlanification && !hasNotPlanification) {
      // PLANIFIED: seulement les propriétés avec planification
      if (!property.planifiedAt) return false
      
      // Filtrer par date de planification si spécifiée
      if (filters.startPlanificationAt) {
        const startDate = new Date(filters.startPlanificationAt)
        const planifiedDate = new Date(property.planifiedAt)
        if (planifiedDate < startDate) return false
      }
      
      if (filters.endPlanificationAt) {
        const endDate = new Date(filters.endPlanificationAt)
        const planifiedDate = new Date(property.planifiedAt)
        if (planifiedDate > endDate) return false
      }
    } else if (hasNotPlanification && !hasPlanification) {
      // UNPLANIFIED: seulement les propriétés sans planification
      if (property.planifiedAt) return false
    } else if (!hasPlanification && !hasNotPlanification) {
      // NONE: aucun filtre de planification actif, accepter tout
    }

    // Filtrer par dates de visite programmée
    if (filters.startScheduledVisitAt) {
      if (!property.scheduledVisitAt) return false
      const startDate = new Date(filters.startScheduledVisitAt)
      const scheduledDate = new Date(property.scheduledVisitAt)
      if (scheduledDate < startDate) return false
    }
    
    if (filters.endScheduledVisitAt) {
      // 'today' est un mot-clé dynamique résolu à l'exécution pour éviter qu'une
      // valeur stockée dans localStorage devienne obsolète au fil des jours.
      const endDateStr = filters.endScheduledVisitAt === 'today'
        ? new Date().toISOString().split('T')[0]
        : filters.endScheduledVisitAt
      // Les lots sans date (jamais planifiés ou statut bloquant) restent toujours
      // visibles : seuls les lots dont la prochaine visite est dans le futur sont masqués.
      if (property.scheduledVisitAt) {
        const scheduledDate = new Date(property.scheduledVisitAt)
        if (scheduledDate > new Date(endDateStr)) return false
      }
    }

    // Filtrer par gestion hors Scaprim
    const hasHorsScaprim = filters.hasHorsScaprim === 'ok' || filters.hasHorsScaprim === true
    if (hasHorsScaprim) {
      // Logique pour identifier les propriétés gérées hors Scaprim
      // À adapter selon la logique métier exacte
      if (!property.managedOutsideScaprim) return false
    }

    // Exclure un ou plusieurs types (filtre invisible pour l'utilisateur)
    // excludeType peut être une string (ex: 'Y') ou un tableau (ex: ['Y', '$'])
    if (filters.excludeType) {
      const excluded = Array.isArray(filters.excludeType) ? filters.excludeType : [filters.excludeType]
      if (excluded.includes(property.type)) return false
    }

    // Filtrer par type de propriété
    if (filters.propertyType && property.type !== filters.propertyType) return false
    if (filters.type && property.type !== filters.type) return false

    // Filtrer par gestionnaire/manager
    if (filters.manager && property.technicalManager?.id !== filters.manager) return false

    // Filtrer par mandant d'assistance
    // Comparaison case-insensitive pour robustesse (UUIDs peuvent être en majuscules depuis MSSQL)
    if (filters.assistance && property.assistance?.mandator?.id?.toLowerCase() !== filters.assistance.toLowerCase()) return false

    // Filtrer par propriétaire légal
    if (filters.owner && property.legalOwner?.id !== filters.owner) return false

    // Filtrer par propriétaire de groupe/mandant gestionnaire
    // Comparaison case-insensitive pour robustesse (UUIDs peuvent être en majuscules depuis MSSQL)
    if (filters.groupOwner && property.manager?.mandator?.id?.toLowerCase() !== filters.groupOwner.toLowerCase()) return false

    // Filtrer par unité (businessUnit est une string, pas un objet)
    if (filters.unit && property.businessUnit !== filters.unit) return false

    // Filtrer par sous-unité (businessSubUnit est une string, pas un objet)
    if (filters.subUnit && property.businessSubUnit !== filters.subUnit) return false

    // Filtrer par NSIL (recherche textuelle)
    if (filters.nsil) {
      const nsilTerm = filters.nsil.toLowerCase()
      const propertyNsil = (property.nsil || '').toLowerCase()
      if (!propertyNsil.includes(nsilTerm)) return false
    }

    // Filtrer par ville/code postal (recherche textuelle)
    if (filters.city) {
      const cityTerm = filters.city.toLowerCase()
      const searchableLocation = [
        property.address?.city,
        property.address?.zipCode,
        property.address?.street
      ].filter(Boolean).join(' ').toLowerCase()
      
      if (!searchableLocation.includes(cityTerm)) return false
    }

    // Filtrer par recherche textuelle générale
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase()
      const searchableText = [
        property.nsil,
        property.propertyCode,
        property.address?.street,
        property.address?.city,
        property.address?.zipCode
      ].filter(Boolean).join(' ').toLowerCase()
      
      if (!searchableText.includes(searchTerm)) return false
    }

    return true
  }

  /**
   * Génère une clé de cache stable à partir des filtres et du tri actif.
   * On exclut le paramètre `limit` car il ne change pas les données filtrées/triées.
   */
  _buildFilteredCacheKey(filters, sortColumn, sortDirection) {
    const filtersKey = JSON.stringify(
      Object.keys(filters)
        .filter(k => k !== 'limit')
        .sort()
        .reduce((acc, k) => { acc[k] = filters[k]; return acc }, {})
    )
    return `${filtersKey}|${sortColumn || ''}|${sortDirection || 'Asc'}`
  }

  /**
   * Invalide tous les caches liés aux propriétés (raw + filtré+trié).
   * À appeler après toute modification de la table properties.
   */
  _invalidateAllPropertyCaches() {
    this.propertiesCache = null
    this.propertiesCacheTimestamp = null
    this.filteredSortedCache = {}
    // Réinitialiser aussi le cache CNR pour forcer un rechargement au prochain filtrage
    this.cnrMandatorIdsCache = null
  }

  /**
   * Pagination optimisée avec cache filtré+trié.
   *
   * Fonctionnement :
   * - Première page avec un jeu de filtres/tri donné : filtre + trie tout le dataset,
   *   stocke le résultat dans `filteredSortedCache[key]`, puis renvoie la tranche demandée.
   * - Pages suivantes (scroll infini) avec les mêmes filtres/tri : le cache est déjà chaud,
   *   on fait un simple Array.slice() → quasi-instantané.
   * - Changement de filtre ou de tri : nouvelle clé de cache → re-calcul une seule fois.
   * - Mutation de données (planification, attribution…) : `_invalidateAllPropertyCaches()`
   *   vide les deux caches, le prochain appel relit IndexedDB.
   */
  async getPaginatedProperties(filters = {}, page = 1, limit = 12, sortColumn = null, sortDirection = 'Asc') {
    try {
      const cacheKey = this._buildFilteredCacheKey(filters, sortColumn, sortDirection)

      // Utiliser le cache filtré+trié s'il existe déjà pour cette combinaison
      if (!this.filteredSortedCache[cacheKey]) {
        // Lecture IndexedDB (ou propertiesCache si déjà chargé) + filtrage JS
        const filteredProperties = await this.filterProperties(filters)

        // Tri côté client si nécessaire
        let sortedProperties = filteredProperties
        if (sortColumn && sortDirection !== 'None') {
          sortedProperties = this.sortPropertiesClientSide(filteredProperties, sortColumn, sortDirection)
        }

        // Stocker le résultat complet (filtré + trié) en cache mémoire
        this.filteredSortedCache[cacheKey] = sortedProperties
      }

      const sortedProperties = this.filteredSortedCache[cacheKey]
      const offset = (page - 1) * limit
      const paginatedProperties = sortedProperties.slice(offset, offset + limit)

      return {
        data: paginatedProperties,
        total: sortedProperties.length,
        page,
        limit,
        hasMore: offset + limit < sortedProperties.length
      }
    } catch (error) {
      console.error('Erreur lors de la pagination des propriétés:', error)
      return {
        data: [],
        total: 0,
        page,
        limit,
        hasMore: false
      }
    }
  }

  /**
   * Retourne tous les IDs des propriétés correspondant aux filtres actifs (sans pagination).
   * Réutilise le cache filteredSortedCache si disponible.
   *
   * @param {Object} filters
   * @param {string|null} sortColumn
   * @param {string} sortDirection
   * @returns {Promise<string[]>}
   */
  async getAllFilteredIds(filters = {}, sortColumn = null, sortDirection = 'Asc') {
    try {
      const cacheKey = this._buildFilteredCacheKey(filters, sortColumn, sortDirection)

      if (!this.filteredSortedCache[cacheKey]) {
        const filteredProperties = await this.filterProperties(filters)
        let sortedProperties = filteredProperties
        if (sortColumn && sortDirection !== 'None') {
          sortedProperties = this.sortPropertiesClientSide(filteredProperties, sortColumn, sortDirection)
        }
        this.filteredSortedCache[cacheKey] = sortedProperties
      }

      return this.filteredSortedCache[cacheKey].map(p => p.id)
    } catch (error) {
      console.error('[dexieService] getAllFilteredIds → erreur:', error)
      return []
    }
  }

  // Tri côté client rapide
  sortPropertiesClientSide(properties, sortColumn, sortDirection) {
    const multiplier = sortDirection === 'Desc' ? -1 : 1
    
    return properties.slice().sort((a, b) => {
      let valueA, valueB
      
      switch (sortColumn) {
        case 'nsil':
          valueA = a.nsil || ''
          valueB = b.nsil || ''
          break
          
        case 'zipCode':
          valueA = a.address?.zipCode || ''
          valueB = b.address?.zipCode || ''
          break
          
        case 'propertyType':
          valueA = a.type || ''
          valueB = b.type || ''
          break
          
        case 'assistanceMandator':
          valueA = a.assistance?.mandator?.id || ''
          valueB = b.assistance?.mandator?.id || ''
          break
          
        case 'technicalManager':
          valueA = a.technicalManager?.fullName || ''
          valueB = b.technicalManager?.fullName || ''
          break
          
        case 'scheduledVisitAt': {
          // Tri simple par date uniquement (sans statut)
          // Les lots sans date vont TOUJOURS à la fin, quel que soit le sens du tri
          const hasDateA = a.scheduledVisitAt && a.scheduledVisitAt !== null
          const hasDateB = b.scheduledVisitAt && b.scheduledVisitAt !== null
          
          if (!hasDateA && !hasDateB) {
            // Ni a ni b n'a de date - les garder dans l'ordre actuel
            return 0
          } else if (!hasDateA) {
            // a sans date va toujours à la fin
            return 1
          } else if (!hasDateB) {
            // b sans date va toujours à la fin
            return -1
          } else {
            // Les deux ont une date - tri normal
            valueA = new Date(a.scheduledVisitAt).getTime()
            valueB = new Date(b.scheduledVisitAt).getTime()
          }
          break
        }
          
        case 'planifiedAt':
          // Utiliser les valeurs pré-calculées si disponibles, sinon calculer
          if (a.planifiedAtSortAsc !== undefined) {
            valueA = a.planifiedAtSortAsc
          } else if (a.planifiedAt) {
            valueA = new Date(a.planifiedAt).getTime()
          } else {
            valueA = Number.MAX_SAFE_INTEGER
          }
          
          if (b.planifiedAtSortAsc !== undefined) {
            valueB = b.planifiedAtSortAsc
          } else if (b.planifiedAt) {
            valueB = new Date(b.planifiedAt).getTime()
          } else {
            valueB = Number.MAX_SAFE_INTEGER
          }
          break
          
        default:
          return 0
      }
      
      if (valueA < valueB) return -1 * multiplier
      if (valueA > valueB) return 1 * multiplier
      
      // En cas d'égalité, trier par ID
      if (a.id < b.id) return -1
      if (a.id > b.id) return 1
      return 0
    })
  }

  // === GESTION DE LA SYNCHRONISATION ===

  async setSyncTimestamp(timestamp = null) {
    try {
      const syncTime = timestamp || new Date().toISOString()
      // Correction : la clé primaire de la table sync est 'entity', pas 'key'
      await sync().put({
        entity: this.SYNC_TIMESTAMP_KEY,
        lastSyncDate: syncTime
      })
      return true
    } catch (error) {
      console.error('Erreur lors de la définition du timestamp de sync:', error)
      return false
    }
  }

  async getSyncTimestamp() {
    try {
      const result = await sync().get(this.SYNC_TIMESTAMP_KEY)
      return result ? result.lastSyncDate : null
    } catch (error) {
      console.error('Erreur lors de la récupération du timestamp de sync:', error)
      return null
    }
  }

  // === GESTION DES DICTIONNAIRES ===

  async saveDictionary(name, data) {
    try {
      await dictionaries().put({
        name,
        data,
        timestamp: new Date().toISOString()
      })
      return true
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du dictionnaire:', error)
      return false
    }
  }

  async getDictionary(name) {
    try {
      const result = await dictionaries().get(name)
      return result ? result.data : {}
    } catch (error) {
      console.error('Erreur lors de la lecture du dictionnaire:', error)
      return {}
    }
  }

  async getAllDictionaries() {
    try {
      const results = await dictionaries().toArray()
      const dictionariesMap = {}
      
      results.forEach(item => {
        dictionariesMap[item.name] = {
          data: item.data,
          timestamp: item.timestamp
        }
      })
      
      return dictionariesMap
    } catch (error) {
      console.error('Erreur lors de la lecture des dictionnaires:', error)
      return {}
    }
  }

  async removeDictionary(name) {
    try {
      await dictionaries().delete(name)
      return true
    } catch (error) {
      console.error('Erreur lors de la suppression du dictionnaire:', error)
      return false
    }
  }

  // === GESTION DE L'AUTHENTIFICATION ===

  async setAuthToken(token) {
    try {
      await auth().put({
        key: this.AUTH_TOKEN_KEY,
        data: token
      })
      return true
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du token auth:', error)
      return false
    }
  }

  async getAuthToken() {
    try {
      const result = await auth().get(this.AUTH_TOKEN_KEY)
      return result ? result.data : null
    } catch (error) {
      console.error('Erreur lors de la récupération du token auth:', error)
      return null
    }
  }

  async removeAuthToken() {
    try {
      await auth().delete(this.AUTH_TOKEN_KEY)
      return true
    } catch (error) {
      console.error('Erreur lors de la suppression du token auth:', error)
      return false
    }
  }

  async setRefreshToken(token) {
    try {
      await auth().put({
        key: this.REFRESH_TOKEN_KEY,
        data: token
      })
      return true
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du refresh token:', error)
      return false
    }
  }

  async getRefreshToken() {
    try {
      const result = await auth().get(this.REFRESH_TOKEN_KEY)
      return result ? result.data : null
    } catch (error) {
      console.error('Erreur lors de la récupération du refresh token:', error)
      return null
    }
  }

  async removeRefreshToken() {
    try {
      await auth().delete(this.REFRESH_TOKEN_KEY)
      return true
    } catch (error) {
      console.error('Erreur lors de la suppression du refresh token:', error)
      return false
    }
  }

  async setUser(user) {
    try {
      await auth().put({
        key: this.USER_KEY,
        data: user
      })
      return true
    } catch (error) {
      console.error('Erreur lors de la sauvegarde de l\'utilisateur:', error)
      return false
    }
  }

  async getUser() {
    try {
      const result = await auth().get(this.USER_KEY)
      return result ? result.data : null
    } catch (error) {
      console.error('Erreur lors de la récupération de l\'utilisateur:', error)
      return null
    }
  }

  async removeUser() {
    try {
      await auth().delete(this.USER_KEY)
      return true
    } catch (error) {
      console.error('Erreur lors de la suppression de l\'utilisateur:', error)
      return false
    }
  }

  // === GESTION DES ÉQUIPES ===

  async getTeams() {
    try {
      return await teams().toArray()
    } catch (error) {
      console.error('Erreur lors de la récupération des équipes:', error)
      return []
    }
  }

  async saveTeams(teamsArray) {
    try {
      await saveMany(teams)(teamsArray)
      return true
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des équipes:', error)
      return false
    }
  }

  // === GESTION DES UTILISATEURS ===

  async getUsers() {
    try {
      return await users().toArray()
    } catch (error) {
      console.error('Erreur lors de la récupération des utilisateurs:', error)
      return []
    }
  }

  async saveUsers(usersArray) {
    try {
      await saveMany(users)(usersArray)
      return true
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des utilisateurs:', error)
      return false
    }
  }

  // === GESTION DES MANDANTS D'ASSISTANCE (CAT) ===

  async saveAssistanceMandators(mandatorsArray) {
    try {
      // Clear + bulkPut pour un remplacement complet (gère aussi les suppressions)
      await assistanceMandators().clear()
      await saveMany(assistanceMandators)(mandatorsArray)
      return true
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des assistance mandators:', error)
      return false
    }
  }

  // === GESTION DES MANDANTS GESTIONNAIRES (GPR) ===

  async saveManagerMandators(mandatorsArray) {
    try {
      // Clear + bulkPut pour un remplacement complet (gère aussi les suppressions)
      await managerMandators().clear()
      await saveMany(managerMandators)(mandatorsArray)
      // Invalider le cache CNR : la liste des mandants GPR vient d'être mise à jour
      this.cnrMandatorIdsCache = null
      return true
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des manager mandators:', error)
      return false
    }
  }

  // === GESTION DES CONTROLS ===

  async getControls() {
    try {
      return await controls().toArray()
    } catch (error) {
      console.error('Erreur lors de la récupération des controls:', error)
      return []
    }
  }

  async saveControls(controlsArray) {
    try {
      // insertedAt supprimé : champ non indexé en V4 et jamais lu dans le code
      await saveMany(controls)(controlsArray)
      return true
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des controls:', error)
      return false
    }
  }

  async countControls() {
    try {
      return await controls().count()
    } catch (error) {
      console.error('Erreur lors du comptage des controls:', error)
      return 0
    }
  }

  // Méthodes pour compatibilité avec l'ancienne API
  teams() {
    return teams()
  }

  users() {
    return users()
  }

  properties() {
    return properties()
  }

  // === UTILITAIRES ===

  async hasLocalData() {
    try {
      const count = await this.countProperties()
      return count > 0
    } catch (error) {
      console.error('Erreur lors de la vérification des données locales:', error)
      return false
    }
  }

  async clearAllData() {
    try {
      await this.db.transaction('rw', [
        properties(),
        sync(),
        dictionaries()
      ], async () => {
        await properties().clear()
        await sync().clear()
        await dictionaries().clear()
      })
      return true
    } catch (error) {
      console.error('Erreur lors du nettoyage des données:', error)
      return false
    }
  }

  async clearAuthData() {
    try {
      await auth().clear()
      return true
    } catch (error) {
      console.error('Erreur lors du nettoyage des données d\'auth:', error)
      return false
    }
  }

  async getLocalDataStats() {
    try {
      const propertiesCount = await this.countProperties()
      const syncTimestamp = await this.getSyncTimestamp()

      return {
        properties: {
          count: propertiesCount,
          lastUpdate: null
        },
        lastSync: syncTimestamp
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques:', error)
      return {
        properties: { count: 0, lastUpdate: null },
        lastSync: null
      }
    }
  }

  // === MIGRATION DES DONNÉES ===

  async migrateFromIndexedDB() {
    try {
      
      // Ouvrir l'ancienne base IndexedDB
      const oldDb = await new Promise((resolve, reject) => {
        const request = indexedDB.open('primview', 2)
        request.onsuccess = () => resolve(request.result)
        request.onerror = () => reject(request.error)
      })

      // Migrer les propriétés
      if (oldDb.objectStoreNames.contains('properties')) {
        const transaction = oldDb.transaction(['properties'], 'readonly')
        const store = transaction.objectStore('properties')
        const properties = await new Promise((resolve, reject) => {
          const request = store.getAll()
          request.onsuccess = () => resolve(request.result)
          request.onerror = () => reject(request.error)
        })

        if (properties.length > 0) {
          await this.savePropertiesBatch(properties)
        }
      }

      oldDb.close()
      return true
    } catch (error) {
      console.error('Erreur lors de la migration:', error)
      return false
    }
  }

  /**
   * Supprime plusieurs lots du cache IndexedDB (soft-delete côté API).
   * Appelé par syncService quand la réponse de /properties/modified contient des `deletedIds`.
   *
   * @param {string[]} ids - Tableau d'UUIDs à supprimer
   */
  async deleteProperties(ids) {
    if (!ids || ids.length === 0) return

    try {
      // `properties` est la fonction module-level définie plus haut dans ce fichier
      await properties().bulkDelete(ids)
      // Invalider les caches locaux pour que la liste se mette à jour
      this._invalidateAllPropertyCaches()
    } catch (error) {
      // Fallback : supprimer un par un
      try {
        for (const id of ids) {
          await properties().delete(id)
        }
        this._invalidateAllPropertyCaches()
      } catch (err) {
        console.error('[DexieService] Erreur deleteProperties:', err)
      }
    }
  }

  /**
   * Retourne tous les IDs de properties présents en IndexedDB.
   * Utilisé pour calculer les IDs orphelins après un import ZIP.
   *
   * @returns {Promise<string[]>}
   */
  async getAllPropertyIds() {
    try {
      const ids = []
      await properties().each(prop => ids.push(prop.id))
      return ids
    } catch (error) {
      console.error('[DexieService] Erreur getAllPropertyIds:', error)
      return []
    }
  }

  /**
   * Retourne tous les IDs de controls présents en IndexedDB.
   * Utilisé pour calculer les controls orphelins après un import ZIP.
   *
   * @returns {Promise<string[]>}
   */
  async getAllControlIds() {
    try {
      const ids = []
      await controls().each(ctrl => ids.push(ctrl.id))
      return ids
    } catch (error) {
      console.error('[DexieService] Erreur getAllControlIds:', error)
      return []
    }
  }

  /**
   * Supprime plusieurs controls du cache IndexedDB.
   * Appelé après un import ZIP pour purger les controls orphelins
   * (appartenant à des visites qui ne font plus partie du top 4 d'un lot).
   *
   * @param {string[]} ids - Tableau d'UUIDs à supprimer
   */
  async deleteControls(ids) {
    if (!ids || ids.length === 0) return

    try {
      await controls().bulkDelete(ids)
    } catch (error) {
      // Fallback : supprimer un par un
      try {
        for (const id of ids) {
          await controls().delete(id)
        }
      } catch (err) {
        console.error('[DexieService] Erreur deleteControls:', err)
      }
    }
  }
}

// Export d'une instance unique
export default new DexieService()
